<template>
    <!-- *********************************************
    *               Card Info Block                  *
    ********************************************** -->
    <divider-generic
    dividerSize="medium"
    icon="credit-card"
    :title="t('cards.changePayment.step1.cardTitle')"
    :applyBorder="false"/>
    <!-- Cards Dropdown -->
    <dropdown-card
    id="cardId"
    style="margin-bottom: 0px"
    :params="cardsDropdownParams"
    :options="getCards"
    :selectedOption="cardFormData.cardId"
    @clicked="[emit('extraDataChange', { field: 'cardId', inputValue: $event }), clearFieldError('cardIdParams')]"/>
    <!-- Details Container -->
    <divider-collapsible
    doubleColumn
    :title="t('textBlocks.showDetails')"
    :openTitle="t('textBlocks.hideDetails')"
    startClosed>
        <!-- Text Block -->
        <text-block-generic
        v-for="(entry, index) in commonCardDetailParser(getDisplayCardDetails)"
        :key="index.key"
        :params="entry"
        :applyEllipsis="entry.applyEllipsis"
        :mask="entry.mask"/>
    </divider-collapsible>
    <!-- *********************************************
    *               Method Info Block                *
    ********************************************** -->
    <divider-generic
    dividerSize="medium"
    icon="payment-single"
    :title="t('cards.changePayment.step1.methodTitle')"/>
    <!-- Method Text Block -->
    <text-block-generic :params="currentMethodParams"/>
    <!-- Method Dropdown -->
    <dropdown-generic
    id="method"
    :params="methodDropdownParams"
    :options="methodOptions"
    :selectedOption="cardFormData.method"
    @clicked="[emit('extraDataChange', { field: 'method', inputValue: $event }), clearFieldError('methodParams')]"/>
</template>

<script setup>
import DividerGeneric from '@/components/generic/dividers/Generic'
import DropdownCard from '@/components/generic/dropdowns/cards/Card'
import DividerCollapsible from '@/components/generic/containers/Collapsible'
import DropdownGeneric from '@/components/generic/dropdowns/generic/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'

/* Vue & Assets */
import { ref, computed, watch } from 'vue'
import { getEmptyFieldsIdentifiers } from '@/assets/scripts/objects'
import { getElementOffsetTopPosition } from '@/assets/scripts/htmlManipulators'

/* Aux */
import { commonCardDetailParser } from '@/assets/scripts/parsers/cards'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['extraDataChange', 'scrollTo', 'resetShowMandatoryErrorsProp', 'formErrorsCleared'])

/* mapActions & mapGetters */
const { getCards, getMethodsList } = mapGetters(moduleMapper.CARDS_CHANGE_PAYMENT)

/* props received
@cardFormData {Object} object with info for our current form
@mandatoryList {Array} Array of mandatory keys corresponding with our form
@showMandatoryErrors {Boolean} Prop to trigger display of mandatory errors
@clearFormErrors {Boolean} Prop to remove of form errors
@isMobile { Boolean }
*/
const props = defineProps({
	cardFormData: Object,
	isMobile: Boolean,
	mandatoryList: Array,
	showMandatoryErrors: Boolean,
	clearFormErrors: Boolean
})

const cardsDropdownParams = ref({
	labelText: t('cards.changePayment.step1.cardsDropdownLabel'),
	mandatory: true,
	headerType: 'balance-header',
    withBorder: true
})

const getDisplayCardDetails = computed(() => {
	return getCards.value.find(card => card.id === props.cardFormData.cardId)
})

const methodDropdownParams = ref({
	labelText: t('cards.changePayment.step1.newMethod'),
	placeholder: t('cards.changePayment.step1.newMethodPlaceholder'),
	mandatory: true,
	errorState: false,
	errorMessage: null
})

const currentMethodParams = computed(() => {
	return {
		label: t('cards.changePayment.step1.currentMethod'),
		content: parseInt(getCards.value.find(card => card.id === props.cardFormData.cardId).paymentMethod) + '%'
	}
})

const methodOptions = computed(() => {
	return getMethodsList.value.reduce((methodList, method) => {
		if (method.description !== currentMethodParams.value.content) {
			return methodList.concat({
				id: method.code,
				text: method.description
			})
		}
		return methodList
	}, [])
})

watch(() => props.cardFormData.cardId, (newVal) => {
    if (newVal) {
        emit('extraDataChange', { field: 'method', inputValue: null })
        clearFieldError('methodParams')
    }
})

/**********************************************************************
*                                                                     *
*                        MANDATORY FIELDS LOGIC                       *
*                                                                     *
***********************************************************************/
const allFormFields = ref({
	cardIdParams: cardsDropdownParams.value,
	methodParams: methodDropdownParams.value
})

watch(() => props.showMandatoryErrors, (newValue, oldValue) => {
	if (newValue) showMandatoryErrorsHandler()
	emit('resetShowMandatoryErrorsProp')
})

const showMandatoryErrorsHandler = () => {
	/* Logic to scroll to first emptyField */
	const mandatoryFieldsEmptyList = getEmptyFieldsIdentifiers(props.cardFormData, props.mandatoryList)

	emit('scrollTo', getElementOffsetTopPosition(mandatoryFieldsEmptyList[0]))

	mandatoryFieldsEmptyList.forEach(fieldName => {
		allFormFields.value[`${fieldName}Params`].errorMessage = t('mandatoryFieldError')
		allFormFields.value[`${fieldName}Params`].errorState = true
	})
}

/**********************************************************************
*                                                                     *
*                          CLEAR ERRORS LOGIC                         *
*                                                                     *
***********************************************************************/
watch(() => props.clearFormErrors, (newValue, oldValue) => {
	clearFormErrors()
	emit('formErrorsCleared')
})

const clearFieldError = (fieldName) => {
	allFormFields.value[fieldName].errorMessage = null
	allFormFields.value[fieldName].errorState = false
}

const clearFormErrors = () => {
	for (var fieldName in allFormFields.value) {
		clearFieldError(fieldName)
	}
}
</script>
